import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  can,
  reminderStepLabel,
  type InvoicePolicy,
  SUPERSEDED_FOLLOWUP,
  SUPERSEDED_REMINDER,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { useSession } from '../session';
import { idem } from '../../lib/idempotency';
import { listOf } from '../../lib/format';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import {
  awaitingServer,
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
  unresolved,
  worstError,
  worstState,
} from '../ui/QueryStates';
import { usePendingOverride } from '../ui/usePendingOverride';
import { Field } from '../create/Field';
import { NumberInput } from '../ui/NumberInput';
import { IconSettings } from '../icons';
import { money } from '../money';

/**
 * The routine chases, on a timer.
 *
 * Grouped Quotes / Invoices / Requests, which is how the shop thinks about them and how
 * every competitor lays them out. Each one is a switch and at most one number — no trigger,
 * no condition, no action grammar. An owner running six techs did not buy this to learn one,
 * and the moment there is a builder there is a support burden for every shop that half-built
 * a rule.
 *
 * The invoice switches write `invoice_policies`, the same row "Getting paid" edits. They
 * appear in both places because a shop looking for "chase my invoices" will look in both —
 * but there is one setting underneath, so turning it off here turns it off there. Two copies
 * would be two answers to "am I chasing this customer".
 */
interface Settings {
  quoteFollowupEnabled: boolean;
  quoteFollowupOffsetDays: number[];
  quoteArchiveEnabled: boolean;
  quoteArchiveAfterDays: number;
  requestArchiveEnabled: boolean;
  requestArchiveAfterDays: number;
}

export function AutomationsCard({ id }: { id: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const role = useSession().role;
  const q = useQuery(trpc.automations.get.queryOptions());
  const policyQ = useQuery(trpc.collections.policy.queryOptions());
  const preview = useQuery(trpc.automations.preview.queryOptions());
  /**
   * The billing half of the night.
   *
   * 🔴 This card's Invoices group turns REMINDERS on, and reminders are swept by
   * `collections`, not by `automations`. Reading only its own sweep is what let the card
   * answer *"Nothing — there's nothing due"* on the very nights it had itself scheduled mail
   * to customers. One question, one answer: the preview below is now the whole night.
   */
  const billing = useQuery(trpc.collections.preview.queryOptions());
  const [error, setError] = useState<string | null>(null);

  /**
   * The switch you just flipped, until the server catches up.
   *
   * 🔴 This used to be a MIRROR of the server row (`useEffect` → `setDraft(q.data.settings)`)
   * whose `onError` did nothing but set a message. A refused save therefore left the box
   * CHECKED above a server row that said off, with no refetch to correct it — so the shop
   * read a setting it did not have until the next full page load, and "we chase unpaid
   * invoices" is not a thing to be wrong about. An OVERRIDE cannot drift like that: it is
   * dropped on both outcomes, and what shows underneath is whatever the server last said.
   */
  const settingsOverride = usePendingOverride<Settings>();
  const policyOverride = usePendingOverride<InvoicePolicy>();

  const save = useMutation(
    trpc.automations.update.mutationOptions({
      /**
       * 🔴 DO NOT `await` THE INVALIDATION HERE.
       *
       * An earlier version did, to avoid a flash of the stale cached value between the save
       * landing and the refetch arriving. query-core awaits `onSuccess` BEFORE dispatching
       * `success`, so the mutation stays `pending` for the whole await — and with
       * `networkMode: 'offlineFirst'` plus `retry: 1`, a refetch that pauses (signal drops, or
       * the tab blurs) never settles. The mutation therefore never settles either:
       * `awaitingServer()` stays true and EVERY control on this card is disabled until the
       * connection returns.
       *
       * That is precisely the freeze `awaitingServer`'s own docblock says it exists to prevent,
       * reintroduced through a path the classifier cannot see — the pause is on a QUERY while
       * `isPaused` on the MUTATION is false. A write's lifecycle must not depend on a read.
       *
       * The flash is instead handled by `settled` below: an override whose value the server has
       * caught up on is dropped at RENDER time, so there is no moment where the old value shows.
       */
      onSuccess: () => {
        setError(null);
        void qc.invalidateQueries({ queryKey: trpc.automations.get.queryKey() });
      },
      onError: (e) => {
        setError(e.message);
        settingsOverride.clear();
        void qc.invalidateQueries({ queryKey: trpc.automations.get.queryKey() });
      },
    }),
  );
  const savePolicy = useMutation(
    trpc.collections.updatePolicy.mutationOptions({
      // Same rule as above: never await a read inside a write's success handler.
      onSuccess: () => {
        setError(null);
        void qc.invalidateQueries({ queryKey: trpc.collections.policy.queryKey() });
      },
      onError: (e) => {
        setError(e.message);
        policyOverride.clear();
        void qc.invalidateQueries({ queryKey: trpc.collections.policy.queryKey() });
      },
    }),
  );

  /**
   * 🔴 **The permission boundary runs through the middle of this card.** The three routine
   * switches write `automations.update` (`org:manage`); the Invoices switch writes
   * `collections.updatePolicy` (`invoice:update`). `office` holds the second and not the
   * first — and `office` is exactly the role the route admits to this page. Without these
   * gates the card offers four identical-looking switches to a dispatcher, three of which
   * flip on screen and then fail with `Missing permission org:manage`.
   *
   * ⚠️ A `bookkeeper` can READ both halves (`report:read` + `invoice:read`) and write
   * neither, so for them the whole card is now read-only rather than a page of controls
   * that cannot work.
   */
  const mayEditRoutine = can(role, 'org', 'manage');
  const mayEditInvoices = can(role, 'invoice', 'update');

  const state = worstState(shownState(q, q.data), shownState(policyQ, policyQ.data));
  /**
   * ⚠️ Gated on HAVING NOTHING TO DRAW, not on the phase.
   *
   * 🔴 An earlier version said `state !== 'ready' || !q.data`. query-core keeps `data` on a
   * background error, so a refetch that failed on window focus — 30s `staleTime`, one flaky
   * Wi-Fi moment — replaced switches that were correct and on screen a second ago with
   * "Couldn't load". This card previously rendered its cached draft through exactly that
   * case, so the contract REGRESSED it.
   *
   * An error is only a reason to blank a surface when there is nothing left to show. The
   * phase still chooses the WORDS; data presence chooses whether to speak at all.
   */
  if (!q.data || !policyQ.data) {
    return (
      <SectionCard id={id} title="Automations">
        <UnresolvedState
          state={unresolved(state)}
          what="your automations"
          icon={IconSettings}
          // A refusal wins over a bad day — see `worstError`.
          error={worstError(q.error, policyQ.error)}
          onRetry={() => {
            void q.refetch();
            void policyQ.refetch();
          }}
        />
      </SectionCard>
    );
  }

  // Overrides minus whatever the server has caught up on — see the note on `save`.
  const settingsServer = q.data.settings;
  const policyServer = policyQ.data.policy;
  // `awaitingServer`, not `isPending` — the repo's one word for "asked for, not yet
  // answered", which is the only thing a control needs to know (016).
  const busy = awaitingServer(save) || awaitingServer(savePolicy);
  // Un-answered local writes over each server row. Two overrides because they are two rows
  // with two watermarks: a refetch of the automation settings says nothing about whether the
  // invoice policy has caught up. See `usePendingOverride`.
  const draft = settingsOverride.apply(settingsServer, q.dataUpdatedAt, busy);
  const policy = policyOverride.apply(policyServer, policyQ.dataUpdatedAt, busy);
  const emailOn = q.data.emailOn;

  const set = (patch: Partial<Settings>) => {
    settingsOverride.record(patch, q.dataUpdatedAt);
    save.mutate({ ...patch, ...idem() });
  };
  const setPolicyValue = (patch: Partial<InvoicePolicy>) => {
    policyOverride.record(patch, policyQ.dataUpdatedAt);
    savePolicy.mutate({ ...patch, ...idem() });
  };
  const routineLocked = busy || !mayEditRoutine;
  const invoicesLocked = busy || !mayEditInvoices;
  /**
   * ⚠️ Text boxes get the PERMISSION gate only, never `busy`.
   *
   * 🔴 Routing `busy` into them silently ate the next field's typing: blurring "First nudge"
   * fires the save, `busy` flips, every box in the grid is disabled, the browser blurs the
   * now-disabled box the tab moved to — and the digits go nowhere, with nothing on screen
   * saying so. On the exact pair people tab between.
   *
   * Safe because the override no longer clears on success: two writes in flight merge, and
   * each key is dropped when the server catches up with it. `busy` stays on the TOGGLES,
   * where it is a double-tap guard and steals no keystrokes.
   */
  const routineReadOnly = !mayEditRoutine;

  /**
   * The preview has its OWN state, separate from the card's.
   *
   * The switches are readable the moment `automations.get` lands; the dry run is a second,
   * slower question. Folding it into the card's state would blank the settings behind a
   * skeleton every time the sweep was slow — and folding the card's state into IT is the
   * bug at the top of this file. Two questions, two answers.
   *
   * ⚠️ The rows below are computed with `?? []` and are ONLY safe because the branch that
   * renders them is guarded on `tonight === 'ready'` first. That ordering is the whole fix;
   * `settings-states.test.ts` pins it, because reversing the two ternaries restores the
   * original defect and looks like a tidy-up.
   */
  const tonight = worstState(
    shownState(preview, preview.data),
    shownState(billing, billing.data),
  );
  const followups = (preview.data?.followups ?? []).filter(
    (f) => f.detail !== SUPERSEDED_FOLLOWUP,
  );
  const reminders = (billing.data?.reminders ?? []).filter(
    (r) => r.detail !== SUPERSEDED_REMINDER,
  );
  const fees = billing.data?.fees ?? [];
  const quotesArchived = preview.data?.quotesArchived ?? [];
  const requestsArchived = preview.data?.requestsArchived ?? [];

  return (
    <SectionCard id={id} title="Automations">
      {/* Two rows, two watermarks — either failing to refresh makes the switches below
          older than they look. */}
      <StaleNotice
        show={refreshFailed(q, q.data) || refreshFailed(policyQ, policyQ.data)}
        onRetry={() => {
          void q.refetch();
          void policyQ.refetch();
        }}
      />
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <p className="mb-1 text-sm text-muted-foreground">
        The jobs you&apos;d otherwise have to remember. Every one of these starts switched
        off — nothing is sent or filed away until you turn it on here.
      </p>
      {!emailOn ? (
        <Notice tone="warn">
          Email isn&apos;t switched on for this account yet, so anything that sends will
          be recorded as skipped rather than sent. Filing things away still works.
        </Notice>
      ) : null}
      {!mayEditRoutine || !mayEditInvoices ? (
        <Notice tone="info">
          {!mayEditRoutine && !mayEditInvoices
            ? 'You can see what this shop has switched on, but not change it. Ask an owner.'
            : !mayEditRoutine
              ? 'Quotes and Requests are the owner’s to change. You can still turn invoice chasing on and off.'
              : 'Invoice chasing is set by whoever handles the money here. Quotes and Requests you can change.'}
        </Notice>
      ) : null}

      {/* ── Quotes ────────────────────────────────────────────────────────── */}
      <Group title="Quotes">
        <Toggle
          checked={draft.quoteFollowupEnabled}
          disabled={routineLocked}
          onChange={(on) => set({ quoteFollowupEnabled: on })}
          label="Follow up with people who haven't answered a quote"
        />
        {draft.quoteFollowupEnabled ? (
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <Field label="First nudge, days after sending">
              <NumberInput
                value={draft.quoteFollowupOffsetDays[0] ?? 2}
                label="First follow-up day"
                disabled={routineReadOnly}
                onCommit={(n) =>
                  set({
                    quoteFollowupOffsetDays: [n, draft.quoteFollowupOffsetDays[1] ?? 5],
                  })
                }
              />
            </Field>
            <Field label="Second nudge, days after sending">
              <NumberInput
                value={draft.quoteFollowupOffsetDays[1] ?? 5}
                label="Second follow-up day"
                disabled={routineReadOnly}
                onCommit={(n) =>
                  set({
                    quoteFollowupOffsetDays: [draft.quoteFollowupOffsetDays[0] ?? 2, n],
                  })
                }
              />
            </Field>
          </div>
        ) : null}

        <Toggle
          checked={draft.quoteArchiveEnabled}
          disabled={routineLocked}
          onChange={(on) => set({ quoteArchiveEnabled: on })}
          label="File away quotes nobody answered"
        />
        {draft.quoteArchiveEnabled ? (
          <div className="mt-2 max-w-xs">
            <Field label="Days to wait after sending">
              <NumberInput
                value={draft.quoteArchiveAfterDays}
                label="Days before archiving a quote"
                disabled={routineReadOnly}
                onCommit={(n) => set({ quoteArchiveAfterDays: n })}
              />
            </Field>
            <p className="mt-1 text-xs text-muted-foreground">
              Or sooner, if you set a date the price holds until. Filed-away quotes can be
              sent again — nothing is deleted.
            </p>
          </div>
        ) : null}
      </Group>

      {/* ── Invoices ──────────────────────────────────────────────────────── */}
      <Group title="Invoices">
        <Toggle
          checked={policy.remindersEnabled}
          disabled={invoicesLocked}
          onChange={(on) => setPolicyValue({ remindersEnabled: on })}
          label="Chase invoices that haven't been paid"
        />
        {policy.remindersEnabled ? (
          <div className="mt-2">
            <p className="text-sm text-muted-foreground">
              Reminders go out {listOf(policy.reminderOffsetDays.map(reminderStepLabel))}.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Change the timing under Getting paid — it&apos;s the same setting, so you
              only ever have it turned on in one place.
            </p>
          </div>
        ) : null}
      </Group>

      {/* ── Requests ──────────────────────────────────────────────────────── */}
      <Group title="Requests">
        <Toggle
          checked={draft.requestArchiveEnabled}
          disabled={routineLocked}
          onChange={(on) => set({ requestArchiveEnabled: on })}
          label="File away requests nobody got to"
        />
        {draft.requestArchiveEnabled ? (
          <div className="mt-2 max-w-xs">
            <Field label="Days to wait">
              <NumberInput
                value={draft.requestArchiveAfterDays}
                label="Days before archiving a request"
                disabled={routineReadOnly}
                onCommit={(n) => set({ requestArchiveAfterDays: n })}
              />
            </Field>
            <p className="mt-1 text-xs text-muted-foreground">
              Only ones still waiting on you. You can put any of them back on the list.
            </p>
          </div>
        ) : null}
      </Group>

      {/*
        A real dry run of the real nightly pass, not a description of one — the same
        function the worker calls, with `dryRun` on. An owner should never learn what an
        automation does by receiving the email their customer got.

        ⭐ THE WHOLE NIGHT, from BOTH sweeps. The page used to answer this question twice,
        from one sweep each, and the two answers disagreed: this card said "Nothing —
        there's nothing due" on any night whose only work was invoice reminders, which is
        work its OWN Invoices group had switched on. One question gets one answer, and the
        answer is only true if it is complete.
      */}
      <div className="mt-4 border-t border-border pt-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          What happens tonight
        </p>
        {/*
          🔴 The empty answer here is not an empty LIST — it is the positive claim *"no
          customer of yours will be emailed tonight"*. Rendering that over a dry run still
          loading, or one that failed, tells an owner the routine is quiet on precisely the
          night they came to check it. `?? []` is how this used to be spelled, and `?? []`
          cannot tell those three apart.
        */}
        {/* The night's own notice: two more queries, and the card's line at the top says
            nothing about whether the dry run came back. */}
        <StaleNotice
          show={
            refreshFailed(preview, preview.data) || refreshFailed(billing, billing.data)
          }
          onRetry={() => {
            void preview.refetch();
            void billing.refetch();
          }}
        />
        {!preview.data || !billing.data ? (
          <div className="mt-2">
            <UnresolvedState
              state={unresolved(tonight)}
              what="tonight's run"
              icon={IconSettings}
              error={worstError(preview.error, billing.error)}
              onRetry={() => {
                void preview.refetch();
                void billing.refetch();
              }}
              skeleton="h-10"
            />
          </div>
        ) : followups.length +
            reminders.length +
            fees.length +
            quotesArchived.length +
            requestsArchived.length ===
          0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Nothing — there&apos;s nothing due.
          </p>
        ) : (
          <ul className="mt-2 space-y-1 text-sm">
            {followups.map((f) => (
              <li key={`f-${f.quoteId}-${f.offsetDays}`} className="flex gap-2">
                <span className="text-muted-foreground">Quote #{f.quoteNumber}</span>
                <span>
                  {/* `'due'`, not `'sent'`. The server's preview cannot know a send will
                      succeed and stopped claiming it could — and this card was the OTHER
                      copy of the line that had to change with it. Missing it turned every
                      follow-up that IS going out tonight into "would skip", which is worse
                      than the bug it replaced. Both reviewers found it independently; the
                      guard in `delivery-copy.test.ts` now sweeps every preview card rather
                      than the one file I had already fixed. */}
                  {f.status === 'due' ? 'nudge' : 'would skip'} {f.customerName}
                  {f.detail ? ` — ${f.detail}` : ''}
                </span>
              </li>
            ))}
            {reminders.map((r) => (
              <li key={`rem-${r.invoiceId}-${r.offsetDays}`} className="flex gap-2">
                <span className="text-muted-foreground">
                  Invoice #{r.invoiceNumber ?? '—'}
                </span>
                <span>
                  {r.status === 'due' ? 'remind' : 'would skip'} {r.customerName}
                  {r.detail ? ` — ${r.detail}` : ''}
                </span>
              </li>
            ))}
            {fees.map((f) => (
              <li key={`fee-${f.invoiceId}-${f.sequence}`} className="flex gap-2">
                <span className="text-muted-foreground">
                  Invoice #{f.invoiceNumber ?? '—'}
                </span>
                <span>
                  charge a late fee of {money(f.amountCents)} — {f.customerName}
                </span>
              </li>
            ))}
            {quotesArchived.map((a) => (
              <li key={`q-${a.id}`} className="flex gap-2">
                <span className="text-muted-foreground">Quote #{a.number}</span>
                <span>
                  file away — {a.reason.toLowerCase()} ({a.label})
                </span>
              </li>
            ))}
            {requestsArchived.map((a) => (
              <li key={`r-${a.id}`} className="flex gap-2">
                <span className="text-muted-foreground">Request #{a.number}</span>
                <span>
                  file away — {a.reason.toLowerCase()} ({a.label})
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </SectionCard>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-border pt-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  );
}

function Toggle({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (on: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm font-medium">
      <input
        type="checkbox"
        className="size-4"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}
